import { PREVIEW_FRESHNESS, type RunFeedPreviewFreshness } from "@nova-ai/shared"
import type { ReactNode } from "react"

import { Callout } from "../ui/callout"

export function RunPreviewFreshnessNotice({
  freshness,
  needsValidation,
  error,
  children,
}: {
  freshness: RunFeedPreviewFreshness | undefined
  needsValidation: boolean
  error: Error | null
  children?: ReactNode
}) {
  if (freshness?.kind === PREVIEW_FRESHNESS.unknown) {
    return (
      <Callout tone="warning" className="shrink-0 break-words text-sm" role="status">
        <p className="font-medium text-foreground">Freshness unknown — not valid for shipment</p>

        <p className="mt-1 text-foreground">{freshness.message}</p>

        {error && (
          <p className="mt-1 text-foreground">
            Refresh failed. Showing the last readable preview. Reload the page to try again.
          </p>
        )}
      </Callout>
    )
  }
  if (freshness?.kind === PREVIEW_FRESHNESS.historical) {
    const missing = freshness.unsafeProducts.items.filter((product) => !product.readable)
    const missingLabels = missing
      .slice(0, 5)
      .map((product) => product.configSku)
      .join(", ")
    const missingSuffix =
      freshness.unsafeProducts.truncated || missing.length > 5
        ? " More affected products are outside this sample."
        : ""
    return (
      <Callout
        tone="warning"
        className="shrink-0 break-words text-sm"
        role="status"
        aria-live="polite"
      >
        <p className="font-medium text-foreground">
          Historical inspection — not valid for shipment
        </p>

        <p className="mt-1 text-foreground">{freshness.recovery.message}</p>

        <p className="mt-1 text-muted-foreground">
          Latest readable values may change during recovery. Validate again when values are current.
        </p>

        {missing.length > 0 && (
          <p className="mt-1 text-foreground">
            {`No readable values for ${missingLabels}.${missingSuffix}`}
          </p>
        )}

        {error && (
          <p className="mt-1 text-foreground">
            Refresh failed. Showing the last historical preview. Reload the page to try again.
          </p>
        )}

        {children}
      </Callout>
    )
  }
  if (freshness?.kind === PREVIEW_FRESHNESS.current && needsValidation) {
    return (
      <Callout tone="info" className="shrink-0 break-words text-sm" role="status">
        Values are current. Run “Check” again before shipment.
      </Callout>
    )
  }
  return null
}
